/**
 * Emits one `feature_viewed` per route change, resolved back to the feature
 * that owns the route. This single hook is the whole of v1 instrumentation —
 * see featureForPath.ts for why it covers every feature without per-feature
 * code.
 *
 * Called from AppShell (the root route component), which is the one place that
 * sits inside both the router and the auth boundary.
 */

import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { track } from "./client";
import { featureForPath } from "./featureForPath";
import { sanitizePathname } from "./sanitize";

export function usePageTracking(): void {
  // Select just the pathname: subscribing to the whole router state would
  // re-run this on every unrelated router update.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastSeen = useRef<string | null>(null);

  useEffect(() => {
    if (lastSeen.current === pathname) return;
    lastSeen.current = pathname;

    const feature = featureForPath(pathname);

    // $pageview feeds PostHog's built-in web analytics; the SDK attaches the
    // URL itself and sanitize_properties scrubs it on the way out.
    track("$pageview");

    // feature_viewed is ours: the same visit, keyed by feature rather than by
    // URL, so "which features get used" survives any future route rename.
    track("feature_viewed", {
      feature_key: feature?.key ?? "unknown",
      feature_label: feature?.label ?? null,
      nav_group: feature?.group ?? null,
      path: sanitizePathname(pathname),
    });
  }, [pathname]);
}
